import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { AdminStatPoint } from '@qaytim/shared';

export interface TrendSeries {
  /** `AdminStatPoint` ning maydoni — grafik shu kalitni o'qiydi. */
  key: keyof Omit<AdminStatPoint, 'day'>;
  label: string;
  /** `--chart-1` … `--chart-5` dan biri. */
  color: string;
}

export interface TrendChartProps {
  points: AdminStatPoint[];
  series: TrendSeries[];
  /** Ekran o'quvchisi uchun: grafik o'zi faqat SVG. */
  label: string;
}

/**
 * Kunlik dinamika grafigi.
 *
 * **To'ldirilgan maydon, chiziq emas.** O'lchanayotgan narsa — kunlik SANOQ, ya'ni nolga
 * tayanadigan kattalik: maydon ostidagi bo'shliq "qancha" degan savolga chiziqdan yaxshiroq
 * javob beradi. Shu sababdan `Y` o'qi ham har doim noldan boshlanadi — recharts ning
 * standart avtomatik oralig'i to'rttadan beshtaga o'sishni ekran bo'ylab ko'tarilishday
 * ko'rsatardi.
 *
 * **Ranglar CSS o'zgaruvchilaridan.** Grafik ikkala mavzuda ham ishlashi kerak, mavzu esa
 * `.dark` klassi bilan almashadi — rangni JS da tanlash uni almashishga kar qilardi.
 *
 * **Sana yorlig'i qisqartirilgan.** O'ttizta to'liq `YYYY-MM-DD` bir-birining ustiga chiqadi;
 * kun-oy juftligi esa bir oylik oynada noaniqlik tug'dirmaydi.
 */
export function TrendChart({ points, series, label }: TrendChartProps) {
  return (
    <div className="h-72 w-full" role="img" aria-label={label}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <defs>
            {series.map((item) => (
              <linearGradient key={item.key} id={`fill-${item.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={item.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={item.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>

          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            minTickGap={24}
            tickFormatter={(day: string) => day.slice(5)}
          />
          <YAxis
            allowDecimals={false}
            domain={[0, 'auto']}
            tickLine={false}
            axisLine={false}
            width={44}
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
          />
          <Tooltip
            // Kursor ostidagi vertikal chiziq: sichqoncha grafikning istalgan balandligida
            // tursa ham, qaysi KUN o'qilayotgani ko'rinib turishi kerak — ayniqsa uchta
            // seriya bir-biriga yaqin ketayotgan joyda.
            cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
            contentStyle={{
              background: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              boxShadow: 'var(--shadow-3)',
              color: 'var(--popover-foreground)',
              fontSize: 12,
            }}
            labelStyle={{ color: 'var(--muted-foreground)', marginBottom: 4 }}
            itemStyle={{ padding: '1px 0' }}
            animationDuration={140}
          />
          <Legend
            wrapperStyle={{ fontSize: 12, color: 'var(--muted-foreground)' }}
            iconType="circle"
          />

          {series.map((item) => (
            <Area
              key={item.key}
              type="monotone"
              dataKey={item.key}
              name={item.label}
              stroke={item.color}
              strokeWidth={2}
              fill={`url(#fill-${item.key})`}
              // Har kunda nuqta chizish o'ttiz kunlik oynada grafikni marjonga aylantirardi;
              // sichqoncha ostidagi nuqta esa qoladi.
              dot={false}
              /*
               * Faol nuqta — diametri 9px va atrofida fon rangidagi halqa.
               *
               * Ilgari u 7px edi va halqasiz: uchta seriya kesishgan joyda nuqta ostidagi
               * chiziqlar bilan qo'shilib ketardi, ya'ni "qaysi seriya o'qilyapti" degan
               * savolga javob faqat maslahat oynasida qolardi. Halqa nuqtani har qanday
               * fondan ajratadi.
               */
              activeDot={{ r: 4.5, strokeWidth: 2, stroke: 'var(--card)' }}
              animationDuration={700}
              animationEasing="ease-out"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
